import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { featuredPropertiesService } from '../../../services/featuredPropertiesService';

const FeaturedPropertyModal = ({
  isOpen,
  onClose,
  featuredProperty = null,
  onSave
}) => {
  const [formData, setFormData] = useState({
    display_order: 1,
    is_active: true,
    start_date: '',
    end_date: '',
    hero_image_url: '',
    custom_headline: '',
    promotional_price: '',
    promotional_text: '',
    listing_type: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form data
  useEffect(() => {
    if (featuredProperty) {
      setFormData({
        display_order: featuredProperty?.display_order || 1,
        is_active: featuredProperty?.is_active ?? true,
        start_date: featuredProperty?.start_date ? 
          new Date(featuredProperty?.start_date)?.toISOString()?.slice(0, 16) : '',
        end_date: featuredProperty?.end_date ? 
          new Date(featuredProperty?.end_date)?.toISOString()?.slice(0, 16) : '',
        hero_image_url: featuredProperty?.hero_image_url || '',
        custom_headline: featuredProperty?.custom_headline || '',
        promotional_price: featuredProperty?.promotional_price || '',
        promotional_text: featuredProperty?.promotional_text || '',
        listing_type: featuredProperty?.listing_type || ''
      });
    } else {
      // Reset form for new property
      setFormData({
        display_order: 1,
        is_active: true,
        start_date: '',
        end_date: '',
        hero_image_url: '',
        custom_headline: '',
        promotional_price: '',
        promotional_text: '',
        listing_type: ''
      });
    }
  }, [featuredProperty]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        start_date: formData?.start_date ? new Date(formData?.start_date)?.toISOString() : null,
        end_date: formData?.end_date ? new Date(formData?.end_date)?.toISOString() : null,
        promotional_price: formData?.promotional_price ? parseFloat(formData?.promotional_price) : null,
        listing_type: formData?.listing_type || null
      };

      let result;
      if (featuredProperty?.id) {
        // Update existing
        result = await featuredPropertiesService?.updateFeaturedProperty(featuredProperty?.id, submitData);
      } else {
        // This modal is for editing only - creation happens in PropertySelectionModal
        setError('Cette modal est réservée à la modification');
        return;
      }

      if (result?.error) {
        setError(`Erreur lors de la sauvegarde: ${result?.error}`);
        return;
      }

      onSave?.();
      onClose();
    } catch (err) {
      setError(`Erreur inattendue: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-deep max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-heading font-bold text-text-primary">
              {featuredProperty ? 'Modifier la Propriété en Vedette' : 'Nouvelle Propriété en Vedette'}
            </h2>
            {featuredProperty?.property && (
              <p className="text-text-secondary mt-1">
                {featuredProperty?.property?.title}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}

          {/* Property Preview */}
          {featuredProperty?.property && (
            <div className="bg-surface rounded-lg p-4 mb-6">
              <div className="flex items-center gap-4">
                <img
                  src={featuredProperty?.property?.images?.[0] || '/assets/images/no_image.png'}
                  alt={featuredProperty?.property?.title}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <div>
                  <h3 className="font-semibold text-text-primary">
                    {featuredProperty?.property?.title}
                  </h3>
                  <p className="text-sm text-text-secondary">
                    {featuredProperty?.property?.address}
                  </p>
                  <p className="text-sm font-medium text-brand-earth">
                    {(featuredProperty?.property?.price / 1000000)?.toFixed(0)}M XOF
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Settings */}
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Paramètres de Base
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Ordre d'Affichage
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={formData?.display_order}
                    onChange={(e) => handleInputChange('display_order', parseInt(e?.target?.value) || 1)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Type d'Annonce
                  </label>
                  <Select
                    value={formData?.listing_type || ''}
                    onChange={(e) => handleInputChange('listing_type', e?.target?.value || null)}
                  >
                    <option value="">Utiliser le type de la propriété</option>
                    <option value="sale">À Vendre</option>
                    <option value="rental">À Louer</option>
                  </Select>
                  <p className="text-xs text-text-secondary mt-1">
                    Si non défini, le type de la propriété sera utilisé
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Statut
                  </label>
                  <Select
                    value={formData?.is_active ? 'active' : 'inactive'}
                    onChange={(e) => handleInputChange('is_active', e?.target?.value === 'active')}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Date de Début
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData?.start_date}
                    onChange={(e) => handleInputChange('start_date', e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Date de Fin (Optionnel)
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData?.end_date}
                    onChange={(e) => handleInputChange('end_date', e?.target?.value)}
                  />
                </div>
              </div>
            </div>

            {/* Content Settings */}
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Contenu Personnalisé
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    URL de l'Image Hero (Optionnel)
                  </label>
                  <Input
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={formData?.hero_image_url}
                    onChange={(e) => handleInputChange('hero_image_url', e?.target?.value)}
                  />
                  <p className="text-xs text-text-secondary mt-1">
                    Si vide, l'image principale de la propriété sera utilisée
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Titre Personnalisé (Optionnel)
                  </label>
                  <Input
                    type="text"
                    placeholder="Titre accrocheur pour la propriété"
                    value={formData?.custom_headline}
                    onChange={(e) => handleInputChange('custom_headline', e?.target?.value)}
                    maxLength={100}
                  />
                  <p className="text-xs text-text-secondary mt-1">
                    Si vide, le titre de la propriété sera utilisé
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Prix Promotionnel (Optionnel)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Prix en XOF"
                    value={formData?.promotional_price}
                    onChange={(e) => handleInputChange('promotional_price', e?.target?.value)}
                  />
                  <p className="text-xs text-text-secondary mt-1">
                    Si défini, remplacera le prix original
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Texte Promotionnel (Optionnel)
                  </label>
                  <textarea
                    className="w-full p-3 border border-input rounded-lg resize-none"
                    rows={3}
                    placeholder="Message marketing accrocheur"
                    value={formData?.promotional_text}
                    onChange={(e) => handleInputChange('promotional_text', e?.target?.value)}
                    maxLength={200}
                  />
                  <p className="text-xs text-text-secondary mt-1">
                    Maximum 200 caractères
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
              iconName={loading ? "Loader2" : "Save"}
              className={loading ? "animate-spin" : ""}
            >
              {loading ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeaturedPropertyModal;